import { useQuery } from "@tanstack/react-query";
import { Network, RefreshCw, Users } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import { EmployeeSlideOver } from "@/components/org-chart/EmployeeSlideOver";
import { OrgChartView, type OrgChartViewHandle } from "@/components/org-chart/OrgChartView";
import { OrgDashboardView } from "@/components/org-chart/OrgDashboardView";
import { OrgListView } from "@/components/org-chart/OrgListView";
import { OrgSearchBar } from "@/components/org-chart/OrgSearchBar";
import type { OrgEmployee, OrgView } from "@/components/org-chart/types";
import { ViewToggle } from "@/components/org-chart/ViewToggle";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/use-auth";
import { useIsMobile } from "@/hooks/use-mobile";
import { queryClient } from "@/lib/queryClient";
import { orpc } from "@/lib/rpc";
import { matchesSearch } from "@/lib/search-utils";

function getViewFromUrl(): OrgView {
  if (typeof window === "undefined") return "chart";
  const v = new URLSearchParams(window.location.search).get("view");
  if (v === "list") return "list";
  if (v === "dashboard") return "dashboard";
  return "chart";
}

export default function OrgChartPage() {
  const [location] = useLocation();
  const { employee: viewer, hasPermission } = useAuth();
  const showDashboard = hasPermission("org_chart");
  const isMobile = useIsMobile();
  const chartRef = useRef<OrgChartViewHandle>(null);

  const [view, setView] = useState<OrgView>(getViewFromUrl);
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: employees = [], isLoading } = useQuery({
    ...orpc.orgChart.list.queryOptions(),
    staleTime: 60_000,
  });

  // Mobile auto-degrades chart→list. Dashboard stays even on mobile.
  // Regular employees never see the dashboard view; if they crafted ?view=dashboard, fall back to chart.
  const effectiveView: OrgView =
    isMobile && view === "chart" ? "list" : view === "dashboard" && !showDashboard ? "chart" : view;

  const updateView = useCallback((next: OrgView) => {
    setView(next);
    const url = new URL(window.location.href);
    if (next === "chart") url.searchParams.delete("view");
    else url.searchParams.set("view", next);
    window.history.replaceState({}, "", url.toString());
  }, []);

  // Sync internal state when URL changes (e.g., back/forward navigation).
  // biome wants `setView` and `getViewFromUrl` (the dep-graph closure) listed
  // and `location` removed because it's not read inside the body — but
  // `location` IS the trigger we care about (it changes on history nav even
  // when the rest of the closure stays equal-by-reference).
  // biome-ignore lint/correctness/useExhaustiveDependencies: location is the trigger, not consumed inside
  useEffect(() => {
    setView(getViewFromUrl());
  }, [location]);

  const employeeById = useMemo(() => {
    const m = new Map<string, OrgEmployee>();
    for (const e of employees) m.set(e.id, e);
    return m;
  }, [employees]);

  const directReportsByManager = useMemo(() => {
    const m = new Map<string, OrgEmployee[]>();
    for (const e of employees) {
      if (e.directManagerId) {
        const list = m.get(e.directManagerId) ?? [];
        list.push(e);
        m.set(e.directManagerId, list);
      }
    }
    m.forEach((list) => {
      list.sort((a: OrgEmployee, b: OrgEmployee) => a.fullName.localeCompare(b.fullName));
    });
    return m;
  }, [employees]);

  // Total subtree size for each manager (transitive count of everyone below them).
  // Iterative bottom-up computation: sort by depth desc, sum direct reports' sizes + their direct count.
  const subtreeSizeById = useMemo(() => {
    const sizes = new Map<string, number>();
    // For each employee, compute subtree size via memoized DFS
    const compute = (id: string): number => {
      const cached = sizes.get(id);
      if (cached !== undefined) return cached;
      const reports = directReportsByManager.get(id) ?? [];
      let total = reports.length;
      for (const r of reports) total += compute(r.id);
      sizes.set(id, total);
      return total;
    };
    for (const e of employees) compute(e.id);
    return sizes;
  }, [employees, directReportsByManager]);

  const filtered = useMemo(() => {
    if (!search.trim()) return employees;
    return employees.filter(
      (e) =>
        matchesSearch(e.fullName, search) ||
        matchesSearch(e.jobTitle ?? "", search) ||
        matchesSearch(e.department ?? "", search),
    );
  }, [employees, search]);

  const highlightedIds = useMemo(() => {
    if (!search.trim()) return undefined;
    return new Set(filtered.map((e) => e.id));
  }, [filtered, search]);

  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: orpc.orgChart.list.queryOptions().queryKey,
    });
  }, []);

  const handleSearchSubmit = useCallback(() => {
    if (effectiveView !== "chart") return;
    if (filtered.length === 1) {
      chartRef.current?.focusEmployee(filtered[0].id);
    }
  }, [effectiveView, filtered]);

  const selected = selectedId ? (employeeById.get(selectedId) ?? null) : null;
  const selectedManager = selected?.directManagerId
    ? (employeeById.get(selected.directManagerId) ?? null)
    : null;
  const selectedReports = selected ? (directReportsByManager.get(selected.id) ?? []) : [];

  const initialFocusId = viewer && employeeById.has(viewer.id) ? viewer.id : null;

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Organograma"
        description="Explore a estrutura de reportes da empresa."
        icon={<Network className="h-5 w-5" />}
      >
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={isLoading}
          data-testid="button-refresh-org"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Atualizar
        </Button>
      </PageHeader>

      <div className="flex flex-col gap-4 px-6 pb-6 flex-1 min-h-0">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <OrgSearchBar value={search} onChange={setSearch} onSubmit={handleSearchSubmit} />
          {!isMobile && (
            <ViewToggle value={view} onChange={updateView} showDashboard={showDashboard} />
          )}
        </div>

        {isLoading ? (
          <div className="space-y-3 flex-1">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : employees.length === 0 ? (
          <EmptyState />
        ) : effectiveView === "chart" ? (
          <div className="flex-1 min-h-[500px]">
            <OrgChartView
              ref={chartRef}
              employees={employees}
              highlightedIds={highlightedIds}
              onSelect={(e) => setSelectedId(e.id)}
              initialFocusId={initialFocusId}
            />
          </div>
        ) : effectiveView === "dashboard" ? (
          <OrgDashboardView
            employees={employees}
            directReportsByManager={directReportsByManager}
            subtreeSizeById={subtreeSizeById}
          />
        ) : (
          <OrgListView
            employees={filtered}
            managerById={employeeById}
            onSelect={(e) => setSelectedId(e.id)}
          />
        )}
      </div>

      <EmployeeSlideOver
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelectedId(null);
        }}
        employee={selected}
        manager={selectedManager}
        directReports={selectedReports}
        totalSubtreeSize={selected ? (subtreeSizeById.get(selected.id) ?? 0) : 0}
        onSelect={(e) => setSelectedId(e.id)}
      />
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex-1 flex flex-col items-center justify-center text-center gap-3 py-16">
      <Users className="h-10 w-10 text-muted-foreground" />
      <div>
        <h3 className="font-medium">Nenhum colaborador ativo</h3>
        <p className="text-sm text-muted-foreground">
          Cadastre pessoas em{" "}
          <Link href="/employees" className="underline">
            Colaboradores
          </Link>{" "}
          para visualizar o organograma.
        </p>
      </div>
    </div>
  );
}
